import {DEFAULT_ACCENT_COLOR, DEFAULT_BACKGROUND_COLOR} from '@cohort/shared/schema/common';
import {isDark} from '@cohort/shared-frontend/utils/isDark';
import type {Theme} from '@cohort/wallet/components/contexts/ThemeContext';
import ThemeContext from '@cohort/wallet/components/contexts/ThemeContext';
import {Colors} from '@cohort/wallet/lib/Theme';
import type {CampaignStoreWDto} from '@cohort/wallet-schemas/campaign';
import type {MerchantWDto} from '@cohort/wallet-schemas/merchant';
import {useEffect, useState} from 'react';

const useTheme = (): Theme => {
  const [backgroundColor, setBackgroundColor] = useState<string>(DEFAULT_BACKGROUND_COLOR);
  const [accentColor, setAccentColor] = useState<string>(DEFAULT_ACCENT_COLOR);

  const setCampaignTheme = (campaign: CampaignStoreWDto): void => {
    setBackgroundColor(campaign.store.backgroundColorCode ?? DEFAULT_BACKGROUND_COLOR);
    setAccentColor(campaign.store.accentColorCode ?? DEFAULT_ACCENT_COLOR);
  };

  const setMerchantTheme = (merchant: MerchantWDto): void => {
    setBackgroundColor(merchant.backgroundColorCode ?? DEFAULT_BACKGROUND_COLOR);
    setAccentColor(merchant.accentColorCode ?? DEFAULT_ACCENT_COLOR);
  };

  const hasDarkBg = isDark(backgroundColor);
  const hasDarkAccent = isDark(accentColor);

  useEffect(() => {
    document.body.style.color = hasDarkBg ? Colors.White : Colors.DarkGray;
    document.body.style.backgroundColor = backgroundColor;
  }, [accentColor, backgroundColor, hasDarkBg]);

  return {
    setCampaignTheme,
    setMerchantTheme,
    backgroundColor,
    accentColor,
    hasDarkBg,
    hasDarkAccent,
  };
};

export const ThemeContextProvider = ({children}: {children: React.ReactNode}): JSX.Element => {
  const theme = useTheme();
  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};
